import { memo } from "react";
import { businessStatus, fmtSpeed, isMarine, getVehicleIcon, timeAgo } from "@/lib/fleet";
import { Crosshair, Navigation as RouteArrow, Flag, History, Terminal, X, Power, Info, Cpu, Route, Leaf, Wrench } from "lucide-react";

// LT1.2 — Fiche véhicule en drawer : header + actions FIXES, puis onglets métier.
// Le contenu de chaque onglet est fourni par le parent (children) selon activeTab (aucune duplication de logique).
const TABS = [
  { key: "resume", label: "Résumé", icon: Info },
  { key: "can", label: "CAN", icon: Cpu },
  { key: "trajets", label: "Trajets", icon: Route },
  { key: "eco", label: "Éco", icon: Leaf },
  { key: "maintenance", label: "Maintenance", icon: Wrench },
];

function VehicleDrawer({
  vehicle, cls, following, engineCut, activeTab = "resume", onTabChange, lastSeen, company, navEnabled = true,
  onFollow, onJoin, onTrips, onHistory, onCommands, onCut, onClose, children,
}) {
  if (!vehicle) return null;
  const lp = vehicle.last_position || {};
  const st = businessStatus(vehicle);
  const VIcon = getVehicleIcon(vehicle.icon);
  const Act = ({ testid, onClick, icon: Icon, label, primary }) => (
    <button onClick={onClick} data-testid={testid}
      className={`w-full flex flex-col items-center justify-center gap-1 rounded-lg py-2.5 text-[11px] font-semibold border transition-colors ${primary ? "bg-[#17A2B8] text-white border-[#17A2B8] hover:bg-[#1391a5]" : "bg-secondary/60 border-border text-foreground hover:border-[#17A2B8]"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
  return (
    <div className={cls} data-testid="vehicle-detail">
      <div className="sticky -top-4 -mx-4 px-4 pt-4 pb-0 z-20 bg-card/95 backdrop-blur">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            <VIcon className="w-6 h-6 shrink-0 mt-0.5" style={{ color: st.color }} />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-bold text-base truncate" data-testid="drawer-vehicle-name">{vehicle.name}</p>
                <span className="data text-[11px] text-muted-foreground shrink-0">{vehicle.plate}</span>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color }} />
                <span className="font-medium uppercase tracking-wide" style={{ color: st.color }}>{st.label}</span>
                <span className="text-muted-foreground/60">·</span>
                <span className="data font-semibold" style={{ color: st.color }} data-testid="drawer-speed">{st.fresh ? fmtSpeed(lp.speed, isMarine(vehicle)) : "—"}</span>
                {!st.fresh && <span className="text-[10px] text-muted-foreground normal-case" data-testid="drawer-stale">Dernier état connu{lp.timestamp ? ` · ${timeAgo(lp.timestamp)}` : ""}</span>}
              </div>
              {(company || lastSeen) && (
                <div className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground mt-0.5 truncate" data-testid="drawer-context">
                  {company && <span className="truncate">{company}</span>}
                  {company && lastSeen && <span className="text-muted-foreground/50">·</span>}
                  {lastSeen && <span className="shrink-0">Communiqué {lastSeen}</span>}
                  {lp.timestamp && <span className="text-muted-foreground/50">·</span>}
                  {lp.timestamp && <span className="shrink-0">Position GPS {timeAgo(lp.timestamp)}</span>}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} data-testid="close-detail-btn" title="Fermer" aria-label="Fermer"
            className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-secondary/80 text-foreground hover:bg-destructive hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* Actions — ligne principale (4), puis Commandes, puis action critique isolée */}
        <div className={`grid ${navEnabled ? "grid-cols-4" : "grid-cols-3"} gap-1.5 mt-3`}>
          <Act testid="drawer-follow" onClick={onFollow} icon={Crosshair} label={following ? "Suivi" : "Suivre"} primary={following} />
          {navEnabled && <Act testid="drawer-join" onClick={onJoin} icon={RouteArrow} label="Rejoindre" />}
          <Act testid="drawer-trips" onClick={onTrips} icon={Flag} label="Trajets" />
          <Act testid="drawer-history" onClick={onHistory} icon={History} label="Historique" />
        </div>
        <button onClick={onCommands} data-testid="drawer-commands"
          className="w-full mt-1.5 flex items-center justify-center gap-1.5 rounded-lg py-2 text-[11px] font-semibold border bg-secondary/60 border-border text-foreground hover:border-[#17A2B8] transition-colors">
          <Terminal className="w-4 h-4" /> Commandes
        </button>
        {onCut && (
          <div className="mt-2 pt-2 border-t border-border/70">
            <button onClick={onCut} data-testid="header-cut-btn" title={engineCut ? "Autoriser le démarrage" : "Bloquer le prochain démarrage"}
              className={`w-full flex items-center justify-center gap-1.5 rounded-lg py-2 text-[11px] font-bold transition-colors ${engineCut ? "bg-[#10B981] hover:bg-[#0ea371] text-white" : "bg-destructive hover:bg-red-600 text-white"}`}>
              <Power className="w-3.5 h-3.5" /> {engineCut ? "Autoriser le démarrage" : "Bloquer le démarrage"}
            </button>
          </div>
        )}
        {/* Barre d'onglets — défilement horizontal sur mobile, reste accessible quel que soit l'onglet */}
        <div className="flex gap-1 mt-3 -mx-4 px-4 overflow-x-auto border-b border-border" data-testid="drawer-tabs">
          {TABS.map((t) => {
            const on = activeTab === t.key;
            return (
              <button key={t.key} data-testid={`drawer-tab-${t.key}`} onClick={() => onTabChange && onTabChange(t.key)}
                className={`shrink-0 flex items-center gap-1 px-2.5 py-2 text-[12px] font-semibold border-b-2 -mb-px transition-colors ${on ? "border-[#17A2B8] text-[#17A2B8]" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="pt-3">{children}</div>
    </div>
  );
}

export default memo(VehicleDrawer);
